import { useLocation, useNavigate } from "react-router-dom";
import { Sidebar, Typography } from "@optimizely/axiom";
import classnames from "classnames";

import styles from "./NavigationBar.module.scss";
import { useUserContext } from "../../../providers/UserProvider";
import { getSecondaryNavigation } from "../../../lib/navigation-helpers";
import { NavigationBarMenuItem } from "./NavigationBarMenuItem";
import { useCsmOrganizations } from "../../../hooks/useCsmOrganizations/useCsmOrganizations";
import { IMenuItem, IRoute } from "../../../lib/navigation-types";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";

interface NavigationGroup {
    header: string;
    items: (IMenuItem & { isActive: boolean })[];
}

const groupNavigationTabs = (tabs: (IMenuItem & { isActive: boolean })[]): NavigationGroup[] => {
    const groups: NavigationGroup[] = [];
    const groupMap = new Map<string, NavigationGroup>();

    for (const tab of tabs) {
        if (!tab.group) continue;
        if (!groupMap.has(tab.group)) {
            const group: NavigationGroup = { header: tab.group, items: [] };
            groupMap.set(tab.group, group);
            groups.push(group);
        }
        groupMap.get(tab.group)!.items.push(tab);
    }

    return groups;
};

export const NavigationBar = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { getAvailableRoutes, organizationId } = useUserContext();
    const { restrictedSupportOrgs } = useCsmOrganizations();

    const availableRoutes = getAvailableRoutes();
    const { enabled: showNavigationTooltipFeatureEnabled } = useFeatureFlag(Flags.ENABLE_NAVIGATION_TOOLTIPS);
    const { enabled: groupingsEnabled } = useFeatureFlag(Flags.SHOW_SIDE_NAVIGATION_GROUPINGS);

    const navigationTabs =
        availableRoutes
            ?.filter((r: IRoute) => {
                if (r.menuItem) {
                    const isSupportOrg = restrictedSupportOrgs
                        ? restrictedSupportOrgs.find((o) => o.id === organizationId)
                        : false;

                    return isSupportOrg ? r.menuItem.support : true;
                } else {
                    return false;
                }
            })
            ?.map((route: IRoute) => ({
                ...(route.menuItem as IMenuItem),
                isActive: route.path ? pathname.startsWith(route.menuItem?.href || "") : false
            }))
            .sort((a, b) => {
                return (a?.listOrder || 0) - (b?.listOrder || 0);
            }) ?? [];

    const hasSecondaryNavigation = !!getSecondaryNavigation({ path: pathname });

    const menuItemClasses = classnames(styles["navigation-bar__menu-item"], {
        [styles["navigation-bar__menu-item--collapsed"]]: hasSecondaryNavigation
    });

    const renderMenuItem = (item: IMenuItem) => (
        <NavigationBarMenuItem
            key={item.href}
            {...item}
            className={menuItemClasses}
            showTooltip={showNavigationTooltipFeatureEnabled || hasSecondaryNavigation}
            onClick={(e?: React.MouseEvent) => {
                e?.preventDefault();
                if (item?.href) navigate(item.href);
            }}
        />
    );

    const renderGrouped = () => {
        const grouped = groupNavigationTabs(navigationTabs);
        return grouped.map(({ header, items }) => (
            <div key={header} role="group" aria-label={header}>
                {!hasSecondaryNavigation && (
                    <Typography
                        type="xxs"
                        tag="div"
                        className={styles["navigation-bar__group-header"]}
                        data-cy="nav-group-header"
                    >
                        {header}
                    </Typography>
                )}
                {items.map((item) => renderMenuItem(item))}
            </div>
        ));
    };

    const renderFlat = () => navigationTabs.map((item) => renderMenuItem(item));

    return (
        <Sidebar
            className={styles["navigation-bar"]}
            anchor="left"
            border
            borderColor="#E8E8E8"
            backgroundColor="smoke"
            isOpen={!hasSecondaryNavigation} //TODO: enable this as part of https://jira.sso.episerver.net/browse/CSX-1281 (missing the pop-tips)
            collapsedWidth={60}
            isFullHeight
            width={208}
            docked
            sticky
        >
            <div
                data-cy="navigation-sidebar"
                className={classnames("soft-half--bottom", styles["navigation-bar__main"])}
            >
                {groupingsEnabled ? renderGrouped() : renderFlat()}
            </div>
        </Sidebar>
    );
};

NavigationBar.displayName = "NavigationBar";
